import React, { useEffect, useState } from "react"
import { XCircle } from "react-feather"

import GuidelineCounterForEditor from "../guidelineCounterForEditor"
import TextArea from "../textArea"
import { getLanguage, useLanguage } from "@languages/index"

const GUIDELINE_LIMIT = 10

const PhotoGuidelineRulesEditor = ({
  limit = GUIDELINE_LIMIT,
  guidelines,
  photoGuidelineMap,
  fetchedPhotoGuidelineMap,
  photoGuidelineEtc,
  setPhotoGuidelineMap,
  setPhotoGuidelineEtc,
  showModal,
  showOverlays,
  closeOverlays,
  otherRequestsMaxLength,
}: any) => {
  const [totalCount, setTotalCount] = useState<number>(0)

  const language = useLanguage()
  const languageTexts = {
    ...language.common,
    ...language.component.photoGuidelines,
  }

  // 각 사진 가이드라인 수량 제한 (전체 최대 = limit)
  const getMax = (type) => {
    if (!type) return

    const currentValue = photoGuidelineMap?.get(type)?.count ?? 0
    const result = currentValue + limit - totalCount

    return result
  }

  const handleChange = ({ id, count, ids, descriptions }) => {
    const hasIds = ids && ids.length > 0
    const hasDescriptions = descriptions && descriptions.length > 0
    const params = { count }
    if (hasDescriptions) params["descriptions"] = descriptions
    if (hasIds) params["ids"] = ids

    setPhotoGuidelineMap((prev) => {
      return new Map(prev).set(id, params)
    })
  }

  const handleExceededMaxValue = () => {
    showModal({
      hideCloseButton: true,
      isCompact: true,
      title: languageTexts.modal_title,
      text: languageTexts._modal_text(limit),
    })
  }

  const handleEtcChange = (value) => {
    setPhotoGuidelineEtc({
      ...photoGuidelineEtc,
      value: value,
    })
  }

  const handleClickInfoIcon = (groupName) => {
    const imgSrc = `https://nmodelin20.s3.ap-northeast-2.amazonaws.com/guide/${groupName}_${getLanguage()}.png`

    showOverlays({
      content: () => {
        return renderOverlay({
          imgSrc: imgSrc,
        })
      },
    })
  }

  useEffect(() => {
    const lastTotalCount = totalCount
    let newTotalCount = 0

    photoGuidelineMap.forEach(({ count }) => {
      newTotalCount += count
    })

    if (lastTotalCount === 4 && newTotalCount === 5) {
      showModal({
        hideCloseButton: true,
        isCompact: true,
        text: languageTexts.index_text_3,
      })
    }

    setTotalCount(newTotalCount)
  }, [photoGuidelineMap])

  const renderOverlay = ({ imgSrc }) => {
    return (
      <>
        <div className="relative bg-white">
          <div
            className="cursor-pointer absolute -top-8 left-1/2 -translate-x-1/2"
            onClick={() => {
              closeOverlays()
            }}
          >
            <XCircle stroke="white" />
          </div>

          <img className="w-full h-full cursor-pointer" src={imgSrc} />
        </div>
      </>
    )
  }

  function makeMaxLengthText(value, maxLength) {
    return languageTexts._index_text_4(value.length, maxLength)
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        {guidelines &&
          guidelines.map(({ groupName, text, hasValue }, index) => {
            if (groupName == null) return <hr key={index} className="h-px -mt-2 bg-[#e8e8e8] border-0" />

            let currentData = photoGuidelineMap.get(groupName)
            let fetchedData = fetchedPhotoGuidelineMap.get(groupName)

            return (
              <GuidelineCounterForEditor
                key={index}
                count={currentData?.count ?? 0}
                descriptions={currentData?.descriptions}
                fetchedIds={fetchedData?.ids}
                fetchedDescriptions={fetchedData?.descriptions}
                title={text}
                classNames={index === 0 && ["mt-4"]}
                min={0}
                max={getMax(groupName)}
                data-max={getMax(groupName)}
                hasAdditionalDescriptions={hasValue}
                onClickInfoIcon={() => handleClickInfoIcon(groupName)}
                onChange={({ count, ids, descriptions }) => {
                  handleChange({ id: groupName, count, ids, descriptions })
                }}
                onInput={({ count, ids, descriptions }) => {
                  handleChange({ id: groupName, count, ids, descriptions })
                }}
                onExceededMaxValue={handleExceededMaxValue}
              />
            )
          })}
      </div>
      <hr className="h-px mt-2 bg-[#e8e8e8] border-0" />
      <div className="form-description mt-4">
        <span>{languageTexts.index_text_1}</span>
        {otherRequestsMaxLength != null && (
          <span className="text-xs font-normal whitespace-pre">
            {`  ${makeMaxLengthText(photoGuidelineEtc["value"] ?? "", otherRequestsMaxLength)}`}
          </span>
        )}
      </div>
      <div className="form-input mt-4">
        <TextArea
          bordered
          placeholder={languageTexts.index_text_2}
          value={photoGuidelineEtc["value"] ?? ""}
          onChangeText={(value) => {
            if (otherRequestsMaxLength != null) {
              const maxLength = otherRequestsMaxLength
              if (value.length > maxLength) {
                value = value.slice(0, maxLength)
              }
            }

            handleEtcChange(value)
          }}
        />
      </div>
    </>
  )
}

export default PhotoGuidelineRulesEditor
